import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, ClipboardList, ArrowRight } from "lucide-react";

interface Assessment {
  id: string;
  name: string;
  audit_type: string;
  status: string;
  framework_id: string;
  period_start: string | null;
  period_end: string | null;
  overall_score: number | null;
  conforms_count: number;
  minor_nc_count: number;
  major_nc_count: number;
  critical_nc_count: number;
  pending_count: number;
  framework?: { code: string; name: string; name_id: string | null };
}

interface AssessmentItem {
  id: string;
  control_id: string;
  verdict: string;
  auditor_comment: string | null;
  corrective_action: string | null;
  control: { clause_code: string; title: string; title_id: string | null; criticality: string; sort_order: number; parent_control_id: string | null };
}

const VERDICTS = [
  { value: "pending", label: "Pending", cls: "bg-muted text-muted-foreground" },
  { value: "conform", label: "Conform", cls: "bg-success/10 text-success border-success/20" },
  { value: "minor_nc", label: "Minor NC", cls: "bg-warning/10 text-warning border-warning/20" },
  { value: "major_nc", label: "Major NC", cls: "bg-destructive/15 text-destructive border-destructive/30" },
  { value: "critical_nc", label: "Critical NC", cls: "bg-destructive/30 text-destructive border-destructive/50" },
  { value: "observation", label: "Observation", cls: "bg-primary/10 text-primary border-primary/20" },
  { value: "na", label: "N/A", cls: "bg-muted text-muted-foreground" },
];

interface Props {
  companyId: string;
  frameworks: { id: string; code: string; name: string; name_id: string | null }[];
}

export default function AssessmentsTab({ companyId, frameworks }: Props) {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string>("");
  const [items, setItems] = useState<AssessmentItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  // create form
  const [cName, setCName] = useState("");
  const [cFw, setCFw] = useState("");
  const [cType, setCType] = useState("internal");
  const [cStart, setCStart] = useState("");
  const [cEnd, setCEnd] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (companyId) void loadAll();
  }, [companyId]);

  useEffect(() => {
    if (activeId) void loadItems(activeId);
  }, [activeId]);

  async function loadAll() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("compliance_assessments")
        .select("*, framework:compliance_frameworks(code, name, name_id)")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setAssessments((data || []) as any);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadItems(id: string) {
    const { data, error } = await supabase
      .from("compliance_assessment_items")
      .select("id, control_id, verdict, auditor_comment, corrective_action, control:compliance_controls(clause_code, title, title_id, criticality, sort_order, parent_control_id)")
      .eq("assessment_id", id);
    if (error) { toast.error(e_msg(error)); return; }
    const sorted = ((data || []) as any[]).sort((a, b) => (a.control?.sort_order ?? 0) - (b.control?.sort_order ?? 0) || a.control?.clause_code?.localeCompare(b.control?.clause_code));
    setItems(sorted as any);
  }

  function e_msg(e: any) { return e?.message || "error"; }

  async function createAssessment() {
    if (!cName || !cFw) { toast.error("Nama dan framework wajib"); return; }
    setCreating(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const { data: a, error } = await supabase
        .from("compliance_assessments")
        .insert({
          company_id: companyId, framework_id: cFw, name: cName, audit_type: cType,
          period_start: cStart || null, period_end: cEnd || null, status: "in_progress",
          created_by: u.user?.id, lead_auditor_id: u.user?.id,
        }).select().single();
      if (error) throw error;

      // Pre-populate items for every active control in framework
      const { data: ctrls, error: cErr } = await supabase
        .from("compliance_controls").select("id").eq("framework_id", cFw).eq("is_active", true);
      if (cErr) throw cErr;

      if (ctrls && ctrls.length > 0) {
        const rows = ctrls.map((c: any) => ({ assessment_id: a.id, control_id: c.id, verdict: "pending" }));
        // Chunk insert
        for (let i = 0; i < rows.length; i += 200) {
          const { error: iErr } = await supabase.from("compliance_assessment_items").insert(rows.slice(i, i + 200));
          if (iErr) throw iErr;
        }
      }
      toast.success(`Audit "${cName}" dibuat dengan ${ctrls?.length || 0} klausul`);
      setCreateOpen(false);
      setCName(""); setCStart(""); setCEnd("");
      await loadAll();
      setActiveId(a.id);
    } catch (e: any) {
      toast.error("Gagal: " + e.message);
    } finally {
      setCreating(false);
    }
  }

  async function setVerdict(itemId: string, verdict: string) {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_assessment_items").update({
      verdict, assessed_by: u.user?.id, assessed_at: new Date().toISOString(),
    }).eq("id", itemId);
    if (error) { toast.error(error.message); return; }
    setItems((arr) => arr.map((x) => x.id === itemId ? { ...x, verdict } : x));
    // refresh assessment summary
    void loadAll();
  }

  async function setComment(itemId: string, field: "auditor_comment" | "corrective_action", value: string) {
    await supabase.from("compliance_assessment_items").update({ [field]: value }).eq("id", itemId);
  }

  async function completeAudit(id: string) {
    if (!confirm("Tandai audit selesai?")) return;
    const { error } = await supabase.from("compliance_assessments").update({ status: "completed" }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Audit selesai");
    void loadAll();
  }

  const active = useMemo(() => assessments.find((a) => a.id === activeId), [assessments, activeId]);

  if (loading) return <div className="p-6 text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>;

  // List view
  if (!activeId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Audit assessment matrix — pencatatan verdict per klausul untuk audit internal/eksternal.</p>
          <Button onClick={() => setCreateOpen(true)}><Plus className="w-4 h-4 mr-1" />New Audit</Button>
        </div>

        {assessments.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-muted-foreground">
            <ClipboardList className="w-8 h-8 mx-auto mb-2" />
            Belum ada audit. Klik "New Audit" untuk membuat assessment pertama.
          </CardContent></Card>
        ) : (
          <div className="grid gap-2">
            {assessments.map((a) => (
              <Card key={a.id} className="cursor-pointer hover:border-primary/50" onClick={() => setActiveId(a.id)}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground flex gap-2 mt-1 flex-wrap">
                      <Badge variant="outline">{a.framework?.code}</Badge>
                      <Badge variant="outline">{a.audit_type}</Badge>
                      <Badge variant="outline">{a.status}</Badge>
                      {a.period_start && <span>{a.period_start} → {a.period_end}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{a.overall_score ?? "—"}{a.overall_score != null && "%"}</div>
                    <div className="text-xs text-muted-foreground">
                      ✓{a.conforms_count} · m{a.minor_nc_count} · M{a.major_nc_count} · C{a.critical_nc_count} · ?{a.pending_count}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Buat Audit Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Nama audit</label>
                <Input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Audit Internal SMK3 Q2 2026" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Framework</label>
                <Select value={cFw} onValueChange={setCFw}>
                  <SelectTrigger><SelectValue placeholder="Pilih framework" /></SelectTrigger>
                  <SelectContent>{frameworks.map((f) => <SelectItem key={f.id} value={f.id}>{f.code} — {f.name_id || f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Tipe</label>
                <Select value={cType} onValueChange={setCType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                    <SelectItem value="self">Self-assessment</SelectItem>
                    <SelectItem value="surveillance">Surveillance</SelectItem>
                    <SelectItem value="certification">Certification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Periode mulai</label>
                  <Input type="date" value={cStart} onChange={(e) => setCStart(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Periode akhir</label>
                  <Input type="date" value={cEnd} onChange={(e) => setCEnd(e.target.value)} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>Batal</Button>
              <Button onClick={createAssessment} disabled={creating}>
                {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Buat & Mulai
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Matrix detail view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => { setActiveId(""); setItems([]); }}>← Back</Button>
          <h3 className="text-lg font-semibold mt-1">{active?.name}</h3>
          <div className="text-xs text-muted-foreground flex gap-2 mt-0.5">
            <Badge variant="outline">{active?.framework?.code}</Badge>
            <Badge variant="outline">{active?.audit_type}</Badge>
            <Badge variant="outline">{active?.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">{active?.overall_score ?? "—"}{active?.overall_score != null && "%"}</div>
            <div className="text-xs text-muted-foreground">
              ✓{active?.conforms_count} · m{active?.minor_nc_count} · M{active?.major_nc_count} · C{active?.critical_nc_count}
            </div>
          </div>
          {active?.status !== "completed" && (
            <Button onClick={() => completeAudit(active!.id)}>Mark Completed</Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-muted/40 px-3 py-2 text-xs font-medium grid grid-cols-[100px_1fr_220px_1fr] gap-2">
          <div>Klausul</div><div>Kontrol</div><div>Verdict</div><div>Komentar / Tindakan</div>
        </div>
        {items.map((it) => {
          const v = VERDICTS.find((x) => x.value === it.verdict) || VERDICTS[0];
          return (
            <div key={it.id} className="border-t px-3 py-2 grid grid-cols-[100px_1fr_220px_1fr] gap-2 items-start hover:bg-muted/20">
              <div className="font-mono text-xs pt-2">{it.control.clause_code}</div>
              <div className="text-sm pt-1">
                <div>{it.control.title_id || it.control.title}</div>
                {it.control.criticality === "critical" && <Badge variant="outline" className="mt-1 text-[10px] bg-destructive/10 text-destructive border-destructive/20">critical</Badge>}
              </div>
              <div>
                <Select value={it.verdict} onValueChange={(v) => setVerdict(it.id, v)}>
                  <SelectTrigger className={"h-9 " + v.cls}><SelectValue /></SelectTrigger>
                  <SelectContent>{VERDICTS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Textarea
                  defaultValue={it.auditor_comment || ""}
                  onBlur={(e) => setComment(it.id, "auditor_comment", e.target.value)}
                  placeholder="Komentar auditor…"
                  className="min-h-[44px] text-sm"
                />
                {(it.verdict === "minor_nc" || it.verdict === "major_nc" || it.verdict === "critical_nc") && (
                  <Textarea
                    defaultValue={it.corrective_action || ""}
                    onBlur={(e) => setComment(it.id, "corrective_action", e.target.value)}
                    placeholder="Corrective action…"
                    className="min-h-[44px] text-sm border-warning/40"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
